import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useProfile } from '../hooks/useProfile'
import { FirestoreService, DayPlan, MealSlot, SavedRecipe } from '../services/FirestoreService'
import { SavedRecipesService } from '../lib/savedRecipesService'
import LoadingSpinner from './LoadingSpinner'
import Modal from './Modal'
import './MealPlanner.css'

interface WeekPlanState {
  monday: DayPlan;
  tuesday: DayPlan;
  wednesday: DayPlan;
  thursday: DayPlan;
  friday: DayPlan;
  saturday: DayPlan;
  sunday: DayPlan;
}

const emptyDayPlan: DayPlan = { breakfast: null, lunch: null, dinner: null }

const emptyWeekPlan: WeekPlanState = {
  monday: { ...emptyDayPlan },
  tuesday: { ...emptyDayPlan },
  wednesday: { ...emptyDayPlan },
  thursday: { ...emptyDayPlan },
  friday: { ...emptyDayPlan },
  saturday: { ...emptyDayPlan },
  sunday: { ...emptyDayPlan }
}

// Get Monday of current week
function getWeekStart(): Date {
  const now = new Date()
  const dayOfWeek = now.getDay()
  const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
  const monday = new Date(now.setDate(diff))
  monday.setHours(0, 0, 0, 0)
  return monday
}

const MealPlanner: React.FC = () => {
  const { user } = useAuth()
  const { profile } = useProfile()
  const [selectedDay, setSelectedDay] = useState('monday')
  const [weekPlan, setWeekPlan] = useState<WeekPlanState>(emptyWeekPlan)
  const [savedRecipes, setSavedRecipes] = useState<SavedRecipe[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showGroceryList, setShowGroceryList] = useState(false)
  const [groceryList, setGroceryList] = useState<string[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ]

  const mealTypes = [
    { key: 'breakfast', label: 'Breakfast', icon: '🌅' },
    { key: 'lunch', label: 'Lunch', icon: '☀️' },
    { key: 'dinner', label: 'Dinner', icon: '🌙' }
  ]

  // Load saved recipes and existing meal plan
  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        setLoading(false)
        return
      }

      try {
        // Load saved recipes
        const recipes = await SavedRecipesService.getUserRecipes(user.id)
        setSavedRecipes(recipes)

        // Load existing meal plan for current week
        const weekStart = getWeekStart()
        const existingPlan = await FirestoreService.getMealPlan(user.id, weekStart)

        if (existingPlan) {
          setWeekPlan(existingPlan.days)
        }
      } catch (error) {
        console.error('Error loading meal planner data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [user])

  const updateMeal = (day: string, mealType: string, recipeId: string) => {
    const recipe = savedRecipes.find(r => r.id === recipeId)
    if (!recipe) return

    const mealSlot: MealSlot = {
      recipe_id: recipe.id,
      recipe_title: recipe.title,
      servings: recipe.servings
    }

    setWeekPlan(prev => ({
      ...prev,
      [day]: {
        ...prev[day as keyof WeekPlanState],
        [mealType]: mealSlot
      }
    }))
    setHasUnsavedChanges(true)
  }

  const clearMeal = (day: string, mealType: string) => {
    setWeekPlan(prev => ({
      ...prev,
      [day]: {
        ...prev[day as keyof WeekPlanState],
        [mealType]: null
      }
    }))
    setHasUnsavedChanges(true)
  }

  const saveMealPlan = async () => {
    if (!user) return

    setSaving(true)
    try {
      await FirestoreService.saveMealPlan({
        user_id: user.id,
        week_start: getWeekStart(),
        days: weekPlan
      })
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Error saving meal plan:', error)
    } finally {
      setSaving(false)
    }
  }

  const generateGroceryList = useCallback(() => {
    const ingredientsSet = new Set<string>()

    // Collect all ingredients from planned meals
    Object.values(weekPlan).forEach(day => {
      const meals = [day.breakfast, day.lunch, day.dinner]
      meals.forEach(meal => {
        if (meal?.recipe_id) {
          const recipe = savedRecipes.find(r => r.id === meal.recipe_id)
          if (recipe) {
            recipe.ingredients.forEach(ing => ingredientsSet.add(ing.toLowerCase()))
          }
        }
      })
    })

    // Sort alphabetically
    const sortedList = Array.from(ingredientsSet).sort()
    setGroceryList(sortedList)
    setShowGroceryList(true)
  }, [weekPlan, savedRecipes])

  const getPlannedMealsCount = () => {
    let count = 0
    Object.values(weekPlan).forEach(day => {
      if (day.breakfast) count++
      if (day.lunch) count++
      if (day.dinner) count++
    })
    return count
  }

  if (profile?.subscription_tier !== 'premium') {
    return (
      <div className="meal-planner-premium">
        <div className="premium-upgrade">
          <h3>🍽️ Meal Planning - Premium Feature</h3>
          <p>Plan your breakfast, lunch, and dinner for the entire week!</p>
          <div className="upgrade-benefits">
            <ul>
              <li>✅ Weekly meal planning</li>
              <li>✅ Use your saved recipes</li>
              <li>✅ Automatic grocery list</li>
              <li>✅ Save plans for future weeks</li>
            </ul>
          </div>
          <button className="upgrade-button">
            Upgrade to Premium - $7.99/month
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return <LoadingSpinner message="Loading meal planner..." />
  }

  return (
    <div className="meal-planner">
      <div className="planner-header">
        <h2>📅 Weekly Meal Planner</h2>
        <p>Plan your meals for the week using your saved recipes!</p>
        {savedRecipes.length === 0 && (
          <p className="no-recipes-warning">
            ⚠️ No saved recipes yet. Save some recipes from Recipe Discovery to plan your meals.
          </p>
        )}
      </div>

      <div className="day-selector">
        {daysOfWeek.map(day => (
          <button
            key={day.key}
            className={`day-button ${selectedDay === day.key ? 'active' : ''}`}
            onClick={() => setSelectedDay(day.key)}
          >
            {day.label}
          </button>
        ))}
      </div>

      <div className="meal-planning-grid">
        <div className="selected-day-plan">
          <h3>{daysOfWeek.find(d => d.key === selectedDay)?.label} Plan</h3>

          {mealTypes.map(meal => {
            const currentMeal = weekPlan[selectedDay as keyof WeekPlanState][meal.key as keyof DayPlan]

            return (
              <div key={meal.key} className="meal-slot">
                <div className="meal-header">
                  <span className="meal-icon">{meal.icon}</span>
                  <h4>{meal.label}</h4>
                </div>

                <div className="meal-content">
                  {currentMeal ? (
                    <div className="assigned-meal">
                      <span>{currentMeal.recipe_title}</span>
                      <button
                        className="clear-meal"
                        onClick={() => clearMeal(selectedDay, meal.key)}
                        aria-label={`Remove ${currentMeal.recipe_title} from ${meal.label}`}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="empty-meal">
                      <span>No meal planned</span>
                    </div>
                  )}
                </div>

                <div className="meal-selector">
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        updateMeal(selectedDay, meal.key, e.target.value)
                      }
                    }}
                    disabled={savedRecipes.length === 0}
                    aria-label={`Select recipe for ${meal.label}`}
                  >
                    <option value="">
                      {savedRecipes.length === 0 ? 'No recipes available' : 'Choose a recipe...'}
                    </option>
                    {savedRecipes.map(recipe => (
                      <option key={recipe.id} value={recipe.id}>
                        {recipe.title} ({recipe.prep_time + recipe.cook_time}min)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )
          })}
        </div>

        <div className="week-overview">
          <h3>Week Overview</h3>
          <div className="week-grid">
            {daysOfWeek.map(day => (
              <div
                key={day.key}
                className={`day-overview ${selectedDay === day.key ? 'selected' : ''}`}
                onClick={() => setSelectedDay(day.key)}
              >
                <h5>{day.label.slice(0, 3)}</h5>
                <div className="day-meals">
                  {mealTypes.map(meal => {
                    const mealData = weekPlan[day.key as keyof WeekPlanState][meal.key as keyof DayPlan]
                    return (
                      <div
                        key={meal.key}
                        className={`mini-meal ${mealData ? 'planned' : 'empty'}`}
                        title={mealData?.recipe_title || `No ${meal.label.toLowerCase()} planned`}
                      >
                        {meal.icon}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="week-stats">
            <span>{getPlannedMealsCount()} of 21 meals planned</span>
          </div>
        </div>
      </div>

      <div className="planner-actions">
        <button
          className="action-button secondary"
          onClick={generateGroceryList}
          disabled={getPlannedMealsCount() === 0}
          aria-label="Generate grocery list from planned meals"
        >
          🛒 Generate Grocery List
        </button>
        <button
          className="action-button primary"
          onClick={saveMealPlan}
          disabled={saving || !hasUnsavedChanges}
          aria-label="Save meal plan"
        >
          {saving ? 'Saving...' : hasUnsavedChanges ? '💾 Save Meal Plan' : '✓ Saved'}
        </button>
      </div>

      {/* Grocery List Modal */}
      <Modal
        isOpen={showGroceryList}
        onClose={() => setShowGroceryList(false)}
        title="🛒 Grocery List"
      >
        <div className="grocery-list-content">
          {groceryList.length === 0 ? (
            <p>No ingredients needed. Plan some meals first!</p>
          ) : (
            <>
              <p className="grocery-count">{groceryList.length} items needed</p>
              <ul className="grocery-items">
                {groceryList.map((item, index) => (
                  <li key={index} className="grocery-item">
                    <input type="checkbox" id={`item-${index}`} />
                    <label htmlFor={`item-${index}`}>{item}</label>
                  </li>
                ))}
              </ul>
              <button
                className="print-button"
                onClick={() => window.print()}
              >
                🖨️ Print List
              </button>
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default MealPlanner
